
import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import { 
  ArrowLeft, Linkedin, Twitter, Mail, BookOpen, 
  ArrowUpRight, Eye, ThumbsUp, ShieldCheck, Award, 
  Globe, Zap, Fingerprint, MapPin, GraduationCap, Trophy, Lightbulb,
  Clock, Check, BadgeCheck
} from 'lucide-react';

const AuthorProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { registeredUsers, posts, institutionalCALogo } = useApp();
  
  const author = registeredUsers.find(u => u.id === id);
  const authorPosts = posts.filter(p => p.authorId === id && p.status === 'published');

  if (!author) return <Navigate to="/blog" replace />;

  const totalViews = authorPosts.reduce((acc, p) => acc + p.views, 0);
  const totalLikes = authorPosts.reduce((acc, p) => acc + p.likes, 0);

  const isCA = author.designation?.includes('CA') || author.designation?.includes('FCA');

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Light Theme Background Assets */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-indigo-100 blur-[140px] rounded-full opacity-60" />
        <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-blue-100 blur-[140px] rounded-full opacity-40" />
      </div>

      {/* Navigation Header */}
      <div className="fixed top-24 left-0 right-0 z-40 px-6 pointer-events-none">
        <div className="max-w-7xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/80 backdrop-blur-xl border border-slate-200 rounded-2xl text-slate-500 hover:text-indigo-600 transition-all pointer-events-auto group shadow-lg">
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em]">Return to Hub</span>
          </Link>
        </div>
      </div>

      {/* Cinematic Identity Section */}
      <section className="relative pt-48 pb-32 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-20 items-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative z-10 w-full aspect-[4/5] rounded-[4rem] overflow-hidden border border-slate-200 shadow-2xl group">
              <img 
                src={author.avatar || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=800&auto=format&fit=crop'} 
                alt={author.name} 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[2s]" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/40 via-transparent to-transparent opacity-60" />
              
              {isCA && (
                <div className="absolute top-12 right-12 w-20 h-20 bg-white/95 backdrop-blur-2xl border border-white/20 rounded-2xl p-4 shadow-2xl flex items-center justify-center">
                  <img src={institutionalCALogo} alt="Official CA Logo" className="w-full h-full object-contain" />
                </div>
              )}

              <div className="absolute bottom-10 left-10 right-10 flex flex-col gap-4">
                 <div className="flex gap-2">
                    {author.isVerifiedMember && (
                      <span className="px-4 py-1.5 bg-blue-600/90 backdrop-blur-md rounded-full text-[9px] font-black uppercase tracking-widest text-white border border-white/20 shadow-lg flex items-center gap-1.5">
                        <BadgeCheck size={12} className="fill-white text-blue-600" /> Verified Member
                      </span>
                    )}
                    <span className="px-4 py-1.5 bg-white/90 backdrop-blur-md rounded-full text-[9px] font-black uppercase tracking-widest text-indigo-600 border border-slate-200 flex items-center gap-1.5 shadow-sm">
                       <Award size={10} /> {author.designation?.split(',')[0] || 'Member'}
                    </span>
                 </div>
              </div>
            </div>
            {/* Back Decoration */}
            <div className="absolute -top-10 -left-10 w-32 h-32 bg-indigo-200 blur-[60px] rounded-full" />
            <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-blue-200 blur-[80px] rounded-full" />
          </motion.div>

          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="flex items-center gap-8 mb-8">
                 <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 text-[10px] font-black uppercase tracking-[0.4em] shadow-sm">
                    <Fingerprint size={12} />
                    Identity Card
                 </div>
                 {author.location && (
                   <div className="flex items-center gap-2 text-slate-500 text-[10px] font-bold uppercase tracking-widest">
                      <MapPin size={12} className="text-indigo-600" />
                      {author.location}
                   </div>
                 )}
              </div>
              
              <h1 className="text-5xl md:text-8xl font-display font-black text-slate-900 mb-6 tracking-tighter leading-[0.9] flex flex-wrap items-center gap-4">
                <span>
                  {author.name.split(' ')[0]} <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-slate-600 to-indigo-800">
                    {author.name.split(' ').slice(1).join(' ')}
                  </span>
                </span>
                {author.isVerifiedMember && (
                  <BadgeCheck size={48} className="fill-blue-600 text-white ml-2 self-start mt-2" />
                )}
              </h1>
              
              <div className="text-xl md:text-2xl font-medium text-slate-500 mb-10 leading-relaxed max-w-2xl">
                {author.designation || 'Strategic Compliance Architect'}
              </div>

              <p className="text-lg text-slate-600 mb-12 max-w-xl leading-relaxed italic border-l-4 border-indigo-100 pl-8">
                "{author.bio || "Dedicated to architecting clarity within the complex intersections of global financial law and domestic regulatory shifts."}"
              </p>

              <div className="flex flex-wrap gap-12 items-center mb-16">
                 {[
                   { label: 'Analyses published', value: authorPosts.length, icon: BookOpen },
                   { label: 'Cumulative reach', value: `${totalViews}+`, icon: Eye },
                   { label: 'Practice Duration', value: `${author.yearsExperience || 0} Yrs`, icon: Clock }
                 ].map((stat, i) => (
                   <div key={i} className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-slate-900 font-black text-3xl tracking-tight">
                         <stat.icon size={18} className="text-indigo-600" />
                         {stat.value}
                      </div>
                      <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{stat.label}</div>
                   </div>
                 ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <button className="px-10 py-5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-all shadow-xl shadow-indigo-600/20 flex items-center gap-3 active:scale-95">
                  Inquire Services <ArrowUpRight size={18} />
                </button>
                <div className="flex items-center gap-3 px-3">
                  {author.linkedinUrl && (
                    <a href={author.linkedinUrl} target="_blank" rel="noopener noreferrer" className="p-4 bg-white hover:bg-indigo-50 border border-slate-200 rounded-2xl text-slate-500 hover:text-indigo-600 transition-all shadow-sm">
                      <Linkedin size={20} />
                    </a>
                  )}
                  {author.twitterUrl && (
                    <a href={author.twitterUrl} target="_blank" rel="noopener noreferrer" className="p-4 bg-white hover:bg-indigo-50 border border-slate-200 rounded-2xl text-slate-500 hover:text-indigo-600 transition-all shadow-sm">
                      <Twitter size={20} />
                    </a>
                  )}
                  {author.websiteUrl && (
                    <a href={author.websiteUrl} target="_blank" rel="noopener noreferrer" className="p-4 bg-white hover:bg-indigo-50 border border-slate-200 rounded-2xl text-slate-500 hover:text-indigo-600 transition-all shadow-sm">
                      <Globe size={20} />
                    </a>
                  )}
                  <a href={`mailto:${author.email}`} className="p-4 bg-white hover:bg-indigo-50 border border-slate-200 rounded-2xl text-slate-500 hover:text-indigo-600 transition-all shadow-sm">
                    <Mail size={20} />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Expertise & Credentials Grid */}
      <section className="py-32 bg-white border-y border-slate-200 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div className="lg:col-span-1">
               <h2 className="text-3xl font-display font-black text-slate-900 mb-8 tracking-tight">Technical <br /><span className="text-indigo-600">Jurisdiction</span></h2>
               <div className="space-y-12">
                  <div className="space-y-4">
                     <h4 className="flex items-center gap-2 text-slate-400 text-[10px] font-black uppercase tracking-[0.2em]">
                        <GraduationCap size={14} className="text-indigo-600" />
                        Academic Pedigree
                     </h4>
                     <ul className="space-y-2">
                        {author.education?.map((edu, i) => (
                           <li key={i} className="text-slate-700 font-bold text-sm">{edu}</li>
                        )) || <li className="text-slate-500 text-sm">Credentials Pending Verification</li>}
                     </ul>
                  </div>
                  <div className="space-y-4">
                     <h4 className="flex items-center gap-2 text-slate-400 text-[10px] font-black uppercase tracking-[0.2em]">
                        <Trophy size={14} className="text-indigo-600" />
                        Institutional Impact
                     </h4>
                     <ul className="space-y-2">
                        {author.achievements?.map((ach, i) => (
                           <li key={i} className="text-slate-700 font-bold text-sm flex items-start gap-2">
                              <Check className="text-green-500 mt-1 shrink-0" size={14} />
                              {ach}
                           </li>
                        )) || <li className="text-slate-500 text-sm">Achievements Pending Upload</li>}
                     </ul>
                  </div>
               </div>
            </div>
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
               {(author.specializations || ['Sovereign Tax', 'Compliance Ops', 'Global Reporting']).map((spec, i) => (
                 <div key={i} className="p-8 rounded-[2.5rem] bg-slate-50 border border-slate-200 hover:border-indigo-200 transition-all group flex items-start gap-6 shadow-sm hover:shadow-xl">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0 group-hover:scale-110 transition-transform">
                       {i % 2 === 0 ? <Globe size={24} /> : <Zap size={24} />}
                    </div>
                    <div>
                       <h3 className="text-lg font-bold text-slate-900 mb-2">{spec}</h3>
                       <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">Active Capability</p>
                    </div>
                 </div>
               ))}
            </div>
          </div>
        </div>
      </section>

      {/* Branded Philosophy Section */}
      <section className="py-32">
         <div className="max-w-7xl mx-auto px-6">
            <div className="p-12 md:p-24 bg-slate-900 rounded-[4rem] border border-slate-200 relative overflow-hidden group shadow-2xl">
               <div className="absolute top-0 right-0 p-12 text-white/5 pointer-events-none group-hover:text-indigo-500/10 transition-colors">
                  <ShieldCheck size={400} />
               </div>
               <div className="relative z-10 max-w-4xl">
                  <div className="flex items-center gap-3 mb-10 text-indigo-400 font-black uppercase tracking-[0.3em] text-[10px]">
                     <Lightbulb size={18} />
                     Philosophical Framework
                  </div>
                  <h3 className="text-3xl md:text-6xl font-display font-black text-white mb-10 tracking-tighter leading-[1.1]">
                    {author.philosophyTitle || "Engineering Clarity in Complexity"}
                  </h3>
                  <div className="text-slate-400 text-xl md:text-2xl leading-relaxed max-w-3xl font-medium border-l-4 border-indigo-600/30 pl-10 py-2">
                     <p>
                        {author.philosophyText || "Every financial insight published here is verified against high-resolution compliance data, ensuring that your strategic decisions are built on absolute fiscal integrity."}
                     </p>
                  </div>
               </div>
            </div>
         </div>
      </section>

      {/* Intelligence Repository Grid */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-end justify-between mb-20 gap-8">
            <div className="max-w-xl">
               <h2 className="text-4xl md:text-6xl font-display font-black text-slate-900 mb-6 tracking-tighter leading-none">Intelligence <br /><span className="text-slate-500">Repository</span></h2>
               <p className="text-slate-500 text-xl font-medium leading-relaxed">
                 A curated index of technical papers, regulatory audits, and institutional deep-dives.
               </p>
            </div>
            <div className="px-8 py-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
               <span className="text-indigo-600 text-[10px] font-black uppercase tracking-[0.3em]">{authorPosts.length} Active Records</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {authorPosts.map((post, idx) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group relative flex flex-col rounded-[3.5rem] bg-white border border-slate-200 hover:border-indigo-200 transition-all overflow-hidden shadow-lg hover:shadow-2xl h-full"
              >
                <div className="h-56 relative overflow-hidden">
                  <img 
                    src={post.imageUrl || 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2000&auto=format&fit=crop'} 
                    alt={post.title} 
                    className="w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
                  <div className="absolute top-8 left-8">
                     <span className="px-4 py-2 bg-white/90 backdrop-blur-md rounded-xl text-indigo-600 text-[9px] font-black uppercase tracking-widest border border-slate-200 shadow-sm">
                        {post.category}
                     </span>
                  </div>
                </div>

                <div className="p-10 flex flex-col flex-grow relative">
                  <Link to={`/blog/${post.id}`} className="block mb-8">
                    <h3 className="text-2xl font-display font-bold text-slate-900 mb-6 leading-tight group-hover:text-indigo-600 transition-colors tracking-tight">
                      {post.title}
                    </h3>
                    <p className="text-slate-500 text-base leading-relaxed line-clamp-3 font-medium">
                      {post.excerpt}
                    </p>
                  </Link>

                  <div className="pt-10 border-t border-slate-100 mt-auto flex items-center justify-between">
                    <div className="flex items-center gap-6">
                       <div className="flex items-center gap-2 text-slate-400 text-[10px] font-black uppercase tracking-widest">
                          <Eye size={14} className="text-indigo-600" /> {post.views}
                       </div>
                       <div className="flex items-center gap-2 text-slate-400 text-[10px] font-black uppercase tracking-widest">
                          <ThumbsUp size={14} className="text-indigo-600" /> {post.likes}
                       </div>
                    </div>
                    <Link to={`/blog/${post.id}`} className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-white hover:bg-indigo-600 hover:border-indigo-600 hover:shadow-xl transition-all group-hover:rotate-12">
                      <ArrowUpRight size={22} />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Institutional Branding Footer */}
      <section className="py-24 border-t border-slate-200 relative overflow-hidden bg-slate-50">
         <div className="absolute inset-0 bg-indigo-50 blur-[100px] pointer-events-none" />
         <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
            <div className="w-14 h-14 rounded-2xl bg-indigo-600 mx-auto mb-8 flex items-center justify-center text-white text-base font-black shadow-xl ring-4 ring-indigo-100">
               MP
            </div>
            <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.6em] mb-4">
               Institutional Contributor Network
            </p>
            <p className="text-slate-500 text-[9px] font-bold uppercase tracking-widest max-w-sm mx-auto">
               This profile is a verified node in the Sovereign Compliance ecosystem Managed by CA Manthan Paliwal.
            </p>
         </div>
      </section>
    </div>
  );
};

export default AuthorProfile;
